import React from 'react';
import { Box, Container, FormControl, InputLabel, Select, MenuItem, Grid, Tooltip, Button, Divider } from '@mui/material';
import { FilterList, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import DistrictMetricToggles from './DistrictMetricToggles';

const DistrictAnalysisFilters = ({
  filters,
  onFilterChange,
  availableFilters,
  onReset,
}) => {
  const gradeRanges = [
    { value: 'elementary', label: 'Elementary', description: 'Pre-K through Grade 5' },
    { value: 'middle', label: 'Middle', description: 'Grades 6 through 8' },
    { value: 'high', label: 'High School', description: 'Grades 9 through 12' }
  ];

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'grey.50',
        backdropFilter: 'blur(8px)',
        zIndex: 1200,
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        py: 1.5,
        mb: 3,
        width: '100%',
        height: 'auto',
        minHeight: 'fit-content',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
      }}
    >
      <Container 
        maxWidth={false}
        sx={{
          px: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Tooltip title="Filters">
              <FilterList 
                color="primary" 
                sx={{ opacity: 0.8, '&:hover': { opacity: 1 }, flexShrink: 0 }} 
              />
            </Tooltip>
  
            <Grid container spacing={2} sx={{ flex: 1, alignItems: 'center' }}>
              {/* Network Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Network</InputLabel>
                  <Select
                    value={filters.network || ''}
                    label="Network"
                    onChange={(e) => onFilterChange('network', e.target.value)}
                  >
                    <MenuItem value="">All Networks</MenuItem>
                    {availableFilters.networks.map((network) => (
                      <MenuItem key={network} value={network}>
                        {network}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* District Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>District</InputLabel>
                  <Select
                    value={filters.district || ''}
                    label="District"
                    onChange={(e) => onFilterChange('district', e.target.value)}
                  >
                    <MenuItem value="">All Districts</MenuItem>
                    {availableFilters.districts.map((district) => (
                      <MenuItem key={district} value={district}>
                        {district}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
 
              {/* City Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>City</InputLabel>
                  <Select
                    value={filters.city || ''}
                    label="City"
                    onChange={(e) => onFilterChange('city', e.target.value)}
                  >
                    <MenuItem value="">All Cities</MenuItem>
                    {availableFilters.cities.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* County Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>County</InputLabel>
                  <Select
                    value={filters.county || ''}
                    label="County"
                    onChange={(e) => onFilterChange('county', e.target.value)}
                  >
                    <MenuItem value="">All Counties</MenuItem>
                    {availableFilters.counties.map((county) => (
                      <MenuItem key={county} value={county}>
                        {county}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
  
              {/* State Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>State</InputLabel>
                  <Select
                    value={filters.state || ''}
                    label="State"
                    onChange={(e) => onFilterChange('state', e.target.value)}
                  >
                    <MenuItem value="">All States</MenuItem>
                    {availableFilters.states.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Grades Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grade Levels</InputLabel>
                  <Select
                    value={filters.grades || ''}
                    label="Grade Levels"
                    onChange={(e) => onFilterChange('grades', e.target.value)}
                  >
                    <MenuItem value="">All Grade Levels</MenuItem>
                    {gradeRanges.map((range) => (
                      <MenuItem key={range.value} value={range.value}>
                        <Tooltip title={range.description} placement="right">
                          <span>{range.label}</span>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

            </Grid>
          </Box>
  
          <Button
            variant="outlined"
            size="small"
            onClick={onReset}
            startIcon={<RestartAltIcon />}
            sx={{
              minWidth: 'auto',
              flexShrink: 0,
              borderColor: 'grey.300',
              color: 'grey.600',
              '&:hover': {
                borderColor: 'grey.400',
                backgroundColor: 'grey.100'
              }
            }}
          >
            Reset
          </Button>
        </Box>
  
        <Divider />
  
        <DistrictMetricToggles 
          filters={filters}
          onFilterChange={onFilterChange}
        />
      </Container>
    </Box>
  );
};

export default DistrictAnalysisFilters;